import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AuraLayout, Layout } from "lola-framework-ui-test";
import { Toast } from "../../components/Toast";
import {
  GALLERY_NOTICE_FIXTURES,
  GalleryInboxChrome,
  GalleryInboxDrawer,
  GalleryPreferencesSheet,
} from "../../components/notifications/GalleryInbox";
import { ConsumerSessionMenu } from "../../components/consumer-auth/ConsumerSessionMenu";
import { TransactionsHistory } from "../../components/TransactionsHistory";
import { PAYOUT_CLAIM_ROUTES } from "../../constants";
import { payoutClaimMockTransactions } from "../../gallery/payoutClaimMock";
import { useStore } from "../../store/globalState";
import type { ITransaction } from "../../interfaces/transactions";
import { Navbar } from "../../components/Navbar";

/**
 * Payout-claim transactions list (gallery).
 *
 * Recipient-side: cash-deposit pending is NOT an actionable Deposit card
 * (only the sender can complete in-person funding). Rows open the receipt.
 *
 * Chrome matches the live widget: no back, no footer, session menu on the left.
 */
export const TransactionsPage = memo(({
  inboxOpen = false,
  toastOpen = false,
  preferencesOpen: preferencesOpenProp = false,
}: {
  inboxOpen?: boolean;
  /** Gallery inspectable: pin the arrival toast so it can be restyled. */
  toastOpen?: boolean;
  /** Gallery inspectable: open the preferences sheet on first paint. */
  preferencesOpen?: boolean;
}) => {
  const { t } = useTranslation("payout-claim");
  const { theme } = useStore();
  const navigate = useNavigate();

  const colorConfig = theme.whiteView;
  const { title, backgroundBtn, textColorBtn } = colorConfig;
  const [noticesOpen, setNoticesOpen] = useState(inboxOpen);
  const [preferencesOpen, setPreferencesOpen] = useState(preferencesOpenProp);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [archivedIds, setArchivedIds] = useState<string[]>([]);
  const notices = GALLERY_NOTICE_FIXTURES.filter(
    (item) => !archivedIds.includes(item.id)
  ).map((item) => ({
    id: item.id,
    title: t(item.titleKey),
    body: t(item.bodyKey),
    isRead: item.read || readIds.includes(item.id),
    createdAt: item.createdAt,
  }));

  const items = useMemo<ITransaction[]>(
    () =>
      payoutClaimMockTransactions.map((item) => ({
        ...item,
        cashDepositPending: false,
        onDeposit: undefined,
        onSelect: item.operationId
          ? () =>
              navigate(`${PAYOUT_CLAIM_ROUTES.RECEIPT}/${item.operationId}`)
          : undefined,
      })),
    [navigate]
  );

  return (
    <>
    <AuraLayout colorConfig={colorConfig} className="need-help-header notifications-home-header">
      <Layout.Header>
        <ConsumerSessionMenu />
        <Navbar
          align="right"
          noBackButton
          icon={
            <GalleryInboxChrome
              ns="payout-claim"
              unread={notices.filter((item) => !item.isRead).length}
              bellColor={title}
              badgeBackground={backgroundBtn}
              badgeTextColor={textColorBtn}
              onOpen={() => setNoticesOpen(true)}
            />
          }
        />
      </Layout.Header>
      <Layout.Content className="transactions-section">
        <TransactionsHistory items={items} />
      </Layout.Content>
    </AuraLayout>
    <GalleryInboxDrawer
      ns="payout-claim"
      items={notices}
      open={noticesOpen}
      onClose={() => setNoticesOpen(false)}
      onMarkRead={(id) =>
        setReadIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
      }
      onArchive={(id) =>
        setArchivedIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
      }
      onOpenPreferences={() => {
        setNoticesOpen(false);
        setPreferencesOpen(true);
      }}
    />
    <GalleryPreferencesSheet
      ns="payout-claim"
      open={preferencesOpen}
      onClose={() => setPreferencesOpen(false)}
    />
    {toastOpen ? (
      <Toast message={t("notifications.exampleToast")} type="info" persist sound />
    ) : null}
  </>
  );
});

export const InboxPage = memo(() => <TransactionsPage inboxOpen />);

export const PreferencesPage = memo(() => <TransactionsPage preferencesOpen />);

export const ToastPage = memo(() => <TransactionsPage toastOpen />);
